/**
 * API Documentation Route
 * Serves Scalar API documentation for Alfie endpoints
 *
 * Uses iframe to properly load and render Scalar documentation
 * with authentication from user's session.
 */

import { EDGE_FUNCTION_HEADERS } from '@/lib/edge-functions';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export const Route = createFileRoute('/api/docs' as any)({
  component: ApiDocsPage,
});

function ApiDocsPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [docHtml, setDocHtml] = useState<string>('');

  useEffect(() => {
    const loadDocs = async () => {
      try {
        const docsUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/alfie-docs`;

        // Get the user's session token
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY;

        const response = await fetch(docsUrl, {
          headers: {
            ...EDGE_FUNCTION_HEADERS,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load docs: ${response.status}`);
        }

        const htmlContent = await response.text();
        setDocHtml(htmlContent);
        setLoading(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load documentation');
        setLoading(false);
        console.error('Error loading API docs:', err);
      }
    };

    loadDocs();
  }, []);

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Error Loading Documentation</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading API documentation...</p>
        </div>
      </div>
    );
  }

  return (
    <iframe
      srcDoc={docHtml}
      className="fixed inset-0 w-full h-full border-0 z-[100]"
      title="Alfie API Documentation"
      sandbox="allow-scripts allow-same-origin"
    />
  );
}
